/**
 * Singleton Google Maps loader to prevent race conditions
 * when multiple components try to load the Google Maps script
 */

interface GoogleMapsLoaderState {
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  callbacks: Set<() => void>;
}

class GoogleMapsLoader {
  private state: GoogleMapsLoaderState = {
    isLoading: false,
    isLoaded: false,
    error: null,
    callbacks: new Set(),
  };

  private scriptId = "google-maps-script";
  private loadTimeoutMs = 10000;

  /**
   * Load Google Maps script and call callback when ready
   */
  async load(apiKey: string, callback: () => void): Promise<void> {
    // If already loaded, call callback immediately
    if (this.state.isLoaded && window.google?.maps) {
      callback();
      return;
    }

    // If there's an error, reject immediately
    if (this.state.error) {
      throw new Error(this.state.error);
    }

    // Add callback to the set
    this.state.callbacks.add(callback);

    // If already loading, just wait for it to complete
    if (this.state.isLoading) {
      return;
    }

    // Start loading
    this.state.isLoading = true;

    try {
      await this.loadScript(apiKey);
      this.state.isLoaded = true;
      this.state.isLoading = false;
      this.state.error = null;

      // Call all pending callbacks
      for (const cb of this.state.callbacks) {
        cb();
      }
      this.state.callbacks.clear();
    } catch (error) {
      this.state.error =
        error instanceof Error ? error.message : "Failed to load Google Maps";
      this.state.isLoading = false;
      this.state.callbacks.clear();
      throw error;
    }
  }

  /**
   * Remove a callback (for cleanup)
   */
  removeCallback(callback: () => void): void {
    this.state.callbacks.delete(callback);
  }

  /**
   * Check if Google Maps is loaded
   */
  isLoaded(): boolean {
    return this.state.isLoaded && !!window.google?.maps;
  }

  /**
   * Get current error state
   */
  getError(): string | null {
    return this.state.error;
  }

  private loadScript(apiKey: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.getElementById(this.scriptId);
      if (existingScript) {
        // Script exists, wait for it to load
        this.waitForExistingScript(resolve, reject);
        return;
      }

      // Create new script
      const script = document.createElement("script");
      script.id = this.scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initGoogleMaps&libraries=marker,geometry&loading=async`;
      script.async = true;

      // Set up global callback
      (window as unknown as { initGoogleMaps: () => void }).initGoogleMaps =
        () => {
          resolve();
        };

      // Set up error handling
      script.onerror = () => {
        reject(new Error("Failed to load Google Maps script"));
      };

      // Set up timeout
      const timeout = setTimeout(() => {
        reject(new Error("Google Maps failed to load within timeout"));
      }, this.loadTimeoutMs);

      // Clean up timeout on success
      const originalCallback = (
        window as unknown as { initGoogleMaps: () => void }
      ).initGoogleMaps;
      (window as unknown as { initGoogleMaps: () => void }).initGoogleMaps =
        () => {
          clearTimeout(timeout);
          originalCallback();
        };

      // Add script to document
      document.head.appendChild(script);
    });
  }

  private waitForExistingScript(
    resolve: () => void,
    reject: (error: Error) => void,
  ): void {
    const checkInterval = 100;
    const maxChecks = this.loadTimeoutMs / checkInterval;
    let checks = 0;

    const interval = setInterval(() => {
      checks++;

      if (window.google?.maps) {
        clearInterval(interval);
        resolve();
        return;
      }

      if (checks >= maxChecks) {
        clearInterval(interval);
        reject(new Error("Google Maps failed to load within timeout"));
      }
    }, checkInterval);
  }
}

// Export singleton instance
export const googleMapsLoader = new GoogleMapsLoader();
